import React, { useEffect, SyntheticEvent, Dispatch } from 'react';

import { Cart } from 'components/Cart';
import { LineItem, Order, FormFieldType, CartStateType, ReducerActionType } from 'utils/types';
import useMediaQuery from 'utils/useMediaQuery';
import { mediaQueries } from '@everlywell/leaves';
import {
  setPromoErrors,
  setCartErrors,
  setLineItems,
  setCurrentOrder,
  setCartTotal,
  setIsOpen,
  setIsPromoApplied,
  setCurrentPromoCode,
  setPromoInputValue,
  setShouldShowPromoInput,
  setShowTOS,
} from './store/actions';
import {
  getLineItems,
  getCurrentOrder,
  addLineItem,
  removeLineItem,
  editLineItem,
  addPromoToOrder,
  removePromos,
} from '../../utils/cartHelpers';

type Props = {
  newProduct?: LineItem;
  productToRemove?: LineItem;
  changeProductQuantity?: Function;
  productToChange?: LineItem;
  cartState: CartStateType;
  dispatch: Dispatch<ReducerActionType>;
};

export const CartContainer = (props: Props) => {
  const { newProduct, productToRemove, productToChange, cartState, dispatch } = props;
  const {
    currentOrder,
    lineItems,
    isOpen,
    shouldShowPromoInput,
    promoInputValue,
  } = cartState;

  const isMobile = useMediaQuery(mediaQueries.forPhoneOnly);

  const onCartTrigger = (response: {
    detail: {
      error: {
        type: string;
        errors: [];
      };
      keepClosed: boolean;
    };
  }) => {
    // May need to display errors in different places,
    // a type is included so that states can be distinguished etc
    const responseDetail = response && response.detail;
    if (responseDetail && responseDetail.error) {
      const {
        detail: {
          error: { type, errors },
        },
      } = response;

      switch (type) {
        case 'promoError':
          setPromoErrors(dispatch, errors);
          break;
        case 'cartError':
          setCartErrors(dispatch, errors);
          break;
        default:
          break;
      }
    }

    const updatedItems = getLineItems();
    const updatedOrder = getCurrentOrder();

    setLineItems(dispatch, updatedItems);
    setCurrentOrder(dispatch, updatedOrder);
    updateCart(updatedItems, updatedOrder);

    if (
      (!responseDetail && !isOpen) ||
      (responseDetail && !responseDetail.keepClosed && !isOpen)
    ) {
      openFloatCart();
    }
  };

  const onSetCartOpen = (response: {
    detail: {
      open: boolean
    }
  }) => {
    const updatedItems = getLineItems();
    const updatedOrder = getCurrentOrder();

    setLineItems(dispatch, updatedItems);
    setCurrentOrder(dispatch, updatedOrder);
    updateCart(updatedItems, updatedOrder);

    const open = response && response.detail && response.detail.open;
    setIsOpen(dispatch, open);
  };

  const updateCart = (updatedItems: LineItem[], updatedOrder?: Order) => {
    let totalPrice;
    let subTotal;

    const items = updatedItems || lineItems;
    const order = updatedOrder || currentOrder;
    const productQuantity = items.reduce((sum, p) => sum + p.quantity, 0);

    if (order && order.promotions && order.promotions.length > 0) {
      totalPrice = parseFloat(order.total);
      subTotal = parseFloat(order.amount);
    } else {
      totalPrice = items.reduce((sum, p) => {
        if (p.base_price) sum += +p.base_price * p.quantity;
        return sum;
      }, 0);
      subTotal = totalPrice;
    }

    setCartTotal(dispatch, {
      productQuantity,
      totalPrice,
      subTotal,
      currencyId: 'USD',
      currencyFormat: '$',
    });
  };

  useEffect(() => {
    // initial cart load
    updateCart(lineItems);

    if (typeof window !== 'undefined') {
      // @ts-ignore
      window.addEventListener('onCartTrigger', onCartTrigger, false);
      // @ts-ignore
      window.addEventListener('onSetCartOpen', onSetCartOpen, false);
    }

    return () => {
      if (typeof window !== 'undefined') {
        // @ts-ignore
        window.removeEventListener('onCartTrigger', onCartTrigger, false);
        // @ts-ignore
        window.removeEventListener('onSetCartOpen', onSetCartOpen, false);
      }
      setIsOpen(dispatch, false);
    };
  }, []);

  useEffect(() => {
    newProduct && addProduct(newProduct);
  }, [newProduct]);

  useEffect(() => {
    productToRemove && removeProduct(productToRemove);
  }, [productToRemove]);

  useEffect(() => {
    productToChange && changeProductQuantity(productToChange);
  }, [productToChange, lineItems]);

  useEffect(() => {
    if (
      currentOrder &&
      currentOrder.promotions &&
      currentOrder.promotions.length > 0
    ) {
      setIsPromoApplied(dispatch, true);
      setCurrentPromoCode(dispatch, currentOrder.promotions[0].code);
    } else {
      setIsPromoApplied(dispatch, false);
    }
  }, [currentOrder]);

  const resetCart = () => {
    setShouldShowPromoInput(dispatch, false);
    setPromoInputValue(dispatch, '');
    setPromoErrors(dispatch, null);
    setCartErrors(dispatch, null);
  };

  const openFloatCart = () => {
    if (isMobile) {
      document.body.style.overflow = 'hidden';
      document.body.style.height = '100vh';
    }
    setIsOpen(dispatch, true);
  };

  const closeFloatCart = () => {
    document.body.style.overflow = '';
    document.body.style.height = '';
    setIsOpen(dispatch, false);
    resetCart();
  };

  const addProduct = (product: LineItem) => {
    let productAlreadyInCart = false;

    lineItems.forEach((item: LineItem) => {
      if (item.variant_id === product.variant_id) {
        item.quantity += product.quantity;
        productAlreadyInCart = true;
      }
    });

    if (!productAlreadyInCart) {
      lineItems.push(product);
      addLineItem(product);
    }

    updateCart(lineItems);
    openFloatCart();
    setCartErrors(dispatch, null);
  };

  const removeProduct = (product: LineItem) => {
    const index = lineItems.findIndex(
      (p: LineItem) => p.variant_id === product.variant_id,
    );
    if (index >= 0) {
      lineItems.splice(index, 1);
      removeLineItem(product);
    }

    updateCart(lineItems);
    setCartErrors(dispatch, null);
  };

  const changeProductQuantity = (changedProduct: LineItem) => {
    const product = lineItems.find(
      (p: LineItem) => p.variant_id === changedProduct.variant_id,
    );
    if (product) {
      product.quantity = changedProduct.quantity;
      if (product.quantity <= 0) {
        removeProduct(product);
      }
      editLineItem(product);
      updateCart(lineItems);
      setCartErrors(dispatch, null);
    }
  };

  const handlePromoChange = (formfield: FormFieldType) => {
    const { value } = formfield;
    setPromoInputValue(dispatch, value);
  };

  const handlePromoLinkChange = () => {
    setShouldShowPromoInput(dispatch, !shouldShowPromoInput);
  };

  const handlePromoApply = (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    addPromoToOrder(currentOrder.id, promoInputValue);
    setPromoErrors(dispatch, null);
  };

  const handleRemovePromo = () => {
    removePromos(currentOrder.id);
    setPromoErrors(dispatch, null);
    setPromoInputValue(dispatch, '');
  };

  const setTOS = (isSet: boolean) => {
    setShowTOS(dispatch, isSet);
  };

  const cartProps = {
    cartState,
    removeProduct,
    changeProductQuantity,
    handlePromoChange,
    handlePromoLinkChange,
    handlePromoApply,
    handleRemovePromo,
    setTOS,
    openFloatCart,
    closeFloatCart,
  };

  return <Cart {...cartProps} />;
};

export default CartContainer;
